/* Shared navigation for the curated product rows. Native scrolling works without JS. */
(() => {
  if (customElements.get('gg-product-carousel')) return;
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-product-track]') || document.getElementById(this.dataset.track);
      this.controls = this.querySelector('[data-product-controls]') || this;
      this.previous = this.querySelector('[data-product-previous]');
      this.next = this.querySelector('[data-product-next]');
      this.status = this.querySelector('[data-product-status]');
      if (!this.track || !this.previous || !this.next) return;
      this.disconnectedCallback();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.scrollTo(event.key === 'Home' ? 0 : this.track.scrollWidth - this.track.clientWidth);
        }
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.track);
      this.update();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }
    scrollTo(left) {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollTo({
        left: Math.max(0, Math.min(maximum, left)),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }
    move(direction) {
      const cards = this.items();
      if (cards.length < 2) return;
      const step = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
      this.scrollTo(this.track.scrollLeft + direction * step);
    }
    update() {
      const cards = this.items();
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.controls.hidden = maximum <= 2 || cards.length < 2;
      const bounds = this.track.getBoundingClientRect();
      const firstCard = cards[0];
      // Legacy tracks start at their first card's scroll-margin, not necessarily zero.
      const start = firstCard ? Math.max(0, firstCard.getBoundingClientRect().left - bounds.left + this.track.scrollLeft - (parseFloat(getComputedStyle(firstCard).scrollMarginLeft) || 0)) : 0;
      this.previous.disabled = this.track.scrollLeft <= start + 2;
      this.next.disabled = this.track.scrollLeft >= maximum - 2;
      if (!this.status) return;
      const visible = cards.map((card, index) => {
        const rect = card.getBoundingClientRect();
        const overlap = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);
        return overlap >= Math.min(rect.width, bounds.width) * 0.5 ? index + 1 : null;
      }).filter(Boolean);
      const first = visible[0] || 0;
      const last = visible[visible.length - 1] || first;
      const label = `${first === last ? first : first + '–' + last} of ${cards.length}`;
      if (this.status.textContent !== label) this.status.textContent = label;
    }
    items() {
      return Array.from(this.track.children).filter(card =>
        this.dataset.itemSelector ? card.matches(this.dataset.itemSelector) :
          card.tagName !== 'SCRIPT' && card.tagName !== 'STYLE' && !card.hidden
      );
    }
  }
  customElements.define('gg-product-carousel', ProductCarousel);
  customElements.define('gg-carousel-controls', class extends ProductCarousel {});
})();
